import React, { useState, useMemo, useCallback } from 'react';
import { useSystemData } from '../../hooks/useSystemData';
import { useDeviceData } from '../../hooks/useDeviceData';
import { useControllerOptions } from '../../hooks/useControllerOptions';
import { useHistoricalData, useHistoryDates } from '../../hooks/useHistoricalData';
import { DashboardHeader } from './DashboardHeader';
import { MetricCard } from './MetricCard';
import { PowerFlowDiagram } from './PowerFlowDiagram';
import { DeviceGrid } from './DeviceGrid';
import { PhaseDetailsCard } from './PhaseDetailsCard';
import { ControllerStatusBar } from './ControllerStatusBar';
import { SettingsModal } from './SettingsModal';
import { PowerHistoryChart, EnergyBarChart, SelfConsumptionGauge } from './Charts';

export function Dashboard() {
  const { data: systemData, loading: systemLoading, lastUpdated } = useSystemData();
  const { data: deviceData, getInverters, getHybrids, getMeter, getBatteryStatus } = useDeviceData();
  const { options, saveOptions, saving } = useControllerOptions();

  // Chart date selection state - managed at Dashboard level to prevent duplicate fetching
  const [selectedChartDate, setSelectedChartDate] = useState<string | null>(null);
  const { dates: availableDates } = useHistoryDates();

  // Fetch historical data based on selected date
  // null = last 24 hours (live), string = specific date
  const { data: historicalData, loading: historyLoading } = useHistoricalData(
    selectedChartDate || 24,
    selectedChartDate ? 0 : 60000  // Only poll for live data
  );

  const [showSettings, setShowSettings] = useState(false);
  const [selectedDeviceType, setSelectedDeviceType] = useState<'pv' | 'hybrid' | null>(null);
  const [showCharts, setShowCharts] = useState(true);

  // Memoize callbacks
  const handleDateChange = useCallback((date: string | null) => {
    setSelectedChartDate(date);
  }, []);

  const formatPower = useCallback((watts: number | undefined) => {
    if (watts === undefined || watts === null) return '0';
    const absWatts = Math.abs(watts);
    if (absWatts >= 1000000) return `${(watts / 1000000).toFixed(1)} MW`;
    if (absWatts >= 1000) return `${(watts / 1000).toFixed(1)} kW`;
    return `${Math.round(watts)} W`;
  }, []);

  const gridStatus = useMemo(() => {
    if (!systemData) return { label: 'Offline', color: 'text-slate-500' };
    const power = systemData.meterPower?.total || 0;
    if (power > 100) return { label: 'Exporting', color: 'text-export' };
    if (power < -100) return { label: 'Importing', color: 'text-import' };
    return { label: 'Balanced', color: 'text-slate-400' };
  }, [systemData]);

  const inverters = getInverters();
  const hybrids = getHybrids();
  const batteryStatus = getBatteryStatus();

  // Calculate hybrid-specific data
  const hybridSolarPower = useMemo(() =>
    hybrids.reduce((sum, h) => sum + (h.data?.solarPower || h.data?.totalSolarPower || 0), 0),
    [hybrids]
  );
  const hybridBatteryPower = useMemo(() =>
    hybrids.reduce((sum, h) => sum + (h.data?.batteryPower || 0), 0),
    [hybrids]
  );
  const hybridBatterySoc = batteryStatus?.averageSOC || 0;
  const regularSolarPower = (systemData?.solarPower || 0) - hybridSolarPower;

  if (systemLoading) {
    return (
      <div className="min-h-screen bg-dashboard-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-solar border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dashboard-bg text-slate-100">
      <DashboardHeader
        lastUpdated={lastUpdated}
        isEmergency={systemData?.deratingEmergency || false}
        onSettingsClick={() => setShowSettings(true)}
      />

      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* Top Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard
            title="Solar Generation"
            value={formatPower(systemData?.solarPower)}
            icon={
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
            }
            accentColor="solar"
            subtitle="Total PV Output"
          />
          <MetricCard
            title="Load Consumption"
            value={formatPower(systemData?.loadPower?.total)}
            icon={
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14l-5-5h3V8h4v4h3l-5 5z"/>
              </svg>
            }
            accentColor="load"
            subtitle="Building Demand"
          />
          <MetricCard
            title="Grid Power"
            value={formatPower(systemData?.meterPower?.total)}
            icon={
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66.19-.34.05-.08.07-.12C8.48 10.94 10.42 7.54 13 3h1l-1 7h3.5c.49 0 .56.33.47.51l-.07.15C12.96 17.55 11 21 11 21z"/>
              </svg>
            }
            accentColor={(systemData?.meterPower?.total || 0) >= 0 ? 'export' : 'import'}
            subtitle={gridStatus.label}
          />
        </div>

        {/* Power Flow Diagram */}
        <PowerFlowDiagram
          solarPower={regularSolarPower}
          loadPower={systemData?.loadPower?.total || 0}
          gridPower={systemData?.meterPower?.total || 0}
          inverterCount={inverters.length}
          hybridCount={hybrids.length}
          hybridSolarPower={hybridSolarPower}
          hybridBatteryPower={hybridBatteryPower}
          hybridBatterySoc={hybridBatterySoc}
        />

        {/* Charts Section */}
        <div className="bg-card-bg border border-slate-700 rounded-lg">
          {/* Charts Header */}
          <button
            onClick={() => setShowCharts(!showCharts)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-800/50 transition-colors rounded-lg"
          >
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="text-sm font-semibold text-slate-300 uppercase tracking-wide">
                Energy Charts
              </span>
              {historicalData.length > 0 && (
                <span className="text-xs text-slate-500">
                  {historicalData.length} points
                </span>
              )}
            </div>
            <svg
              className={`w-5 h-5 text-slate-400 transition-transform ${showCharts ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Charts Content */}
          {showCharts && (
            <div className="p-4 pt-0 space-y-4">
              {/* Power History Chart - Full Width */}
              <PowerHistoryChart
                data={historicalData}
                loading={historyLoading}
                availableDates={availableDates}
                selectedDate={selectedChartDate}
                onDateChange={handleDateChange}
              />

              {/* Self-Consumption Gauge + Energy Bar Chart */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SelfConsumptionGauge data={historicalData} loading={historyLoading} />
                <EnergyBarChart data={historicalData} loading={historyLoading} />
              </div>
            </div>
          )}
        </div>

        {/* Bottom Section: Device Summary Cards + Phase Details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* PV Inverters Card */}
          <div
            className="bg-card-bg border border-slate-700 rounded-lg p-4 cursor-pointer hover:border-solar transition-colors"
            onClick={() => setSelectedDeviceType(selectedDeviceType === 'pv' ? null : 'pv')}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wide">PV Inverters</h3>
              <span className="text-xs px-2 py-1 bg-solar/20 text-solar rounded">{inverters.length} devices</span>
            </div>
            <div className="text-2xl font-bold text-solar mb-1">{formatPower(regularSolarPower)}</div>
            <div className="text-xs text-slate-500">Click to view details</div>
          </div>

          {/* Hybrid Inverters Card */}
          {hybrids.length > 0 && (
            <div
              className="bg-card-bg border border-slate-700 rounded-lg p-4 cursor-pointer hover:border-purple-500 transition-colors"
              onClick={() => setSelectedDeviceType(selectedDeviceType === 'hybrid' ? null : 'hybrid')}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wide">Hybrid Inverters</h3>
                <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded">{hybrids.length} devices</span>
              </div>
              <div className="flex items-center gap-4">
                <div>
                  <div className="text-lg font-bold text-purple-400">{hybridBatterySoc}%</div>
                  <div className="text-xs text-slate-500">Battery SOC</div>
                </div>
                <div className="flex-1">
                  <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${
                        hybridBatterySoc < 20 ? 'bg-red-500' :
                        hybridBatterySoc < 50 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${hybridBatterySoc}%` }}
                    />
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-semibold ${
                    hybridBatteryPower < -100 ? 'text-blue-400' :
                    hybridBatteryPower > 100 ? 'text-orange-400' : 'text-slate-400'
                  }`}>
                    {hybridBatteryPower < -100 ? 'Charging' :
                     hybridBatteryPower > 100 ? 'Discharging' : 'Idle'}
                  </div>
                  <div className="text-xs text-slate-500">{formatPower(Math.abs(hybridBatteryPower))}</div>
                </div>
              </div>
              <div className="text-xs text-slate-500 mt-2">Click to view details</div>
            </div>
          )}

          {/* Phase Details */}
          <PhaseDetailsCard
            voltage={systemData?.meterVolts}
            current={systemData?.meterAmps}
            power={systemData?.meterPower}
            reactivePower={systemData?.meterReactivePower}
          />
        </div>

        {/* Expanded Device Grid (shown when a device type is selected) */}
        {selectedDeviceType && (
          <div className="bg-card-bg border border-slate-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-200">
                {selectedDeviceType === 'pv' ? 'PV Inverter Details' : 'Hybrid Inverter Details'}
              </h3>
              <button
                onClick={() => setSelectedDeviceType(null)}
                className="text-slate-400 hover:text-slate-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <DeviceGrid devices={selectedDeviceType === 'pv' ? inverters : hybrids} />
          </div>
        )}

        {/* Controller Status Bar */}
        <ControllerStatusBar
          deratePower={systemData?.deratePower || 0}
          increasePower={systemData?.increasePower || 0}
          deratingEmergency={systemData?.deratingEmergency || false}
          exportLimitEnabled={options?.exportLimitEnabled || false}
          curtailedPower={systemData?.curtailedPower || 0}
          currentRateLimit={systemData?.currentRateLimit || 10000}
          batteryHeadroom={systemData?.availableBatteryChargeHeadroom || 0}
          batteryAbsorptionExpected={systemData?.batteryAbsorptionExpected || 0}
        />

      </main>

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        options={options}
        onSave={saveOptions}
        saving={saving}
      />
    </div>
  );
}
